/**
 * Subdomain and custom domain detection utility for noknok.pro
 * Detects if user is accessing via subdomain format (e.g., rysonroofing.noknok.pro)
 * or via a custom domain (e.g., joesroofing.com)
 */

const MAIN_DOMAINS = ['noknok.pro', 'noknok-app.pages.dev', 'noknok-pro.pages.dev', 'lovable.app', 'localhost'];

function isDevEnvironment(hostname: string): boolean {
  return hostname === 'localhost' ||
    hostname.startsWith('localhost:') ||
    hostname === '127.0.0.1' ||
    hostname.startsWith('127.0.0.1:') ||
    hostname.includes('.replit.dev') ||
    hostname.includes('.replit.app') ||
    hostname.includes('.repl.co') ||
    hostname.includes('.picard.replit.dev') ||
    hostname.includes('.kirk.replit.dev') ||
    hostname.includes('.spock.replit.dev') ||
    hostname.startsWith('0.0.0.0');
}

export function getSubdomainFromHostname(hostname: string): string | null {
  // Handle development environments
  if (isDevEnvironment(hostname)) {
    return null;
  }

  // Check if it's a lovable.app or lovableproject.com preview domain (ignore subdomains here)
  if (hostname.endsWith('.lovable.app') || hostname.endsWith('.lovableproject.com')) {
    return null;
  }

  // Check if it's the main domain without subdomain
  for (const mainDomain of MAIN_DOMAINS) {
    if (hostname === mainDomain || hostname === `www.${mainDomain}`) {
      return null;
    }
  }

  // Check for subdomain on noknok.pro
  if (hostname.endsWith('.noknok.pro')) {
    const subdomain = hostname.replace('.noknok.pro', '');
    // Ignore www
    if (subdomain === 'www') {
      return null;
    }
    return subdomain;
  }

  return null;
}

/**
 * Detects if the hostname is a custom domain (not a platform subdomain)
 * Returns the full hostname if it's a custom domain, null otherwise
 */
export function getCustomDomainFromHostname(hostname: string): string | null {
  // Handle development environments
  if (isDevEnvironment(hostname)) {
    return null;
  }

  // Skip lovable.app and lovableproject.com preview domains
  if (hostname.endsWith('.lovable.app') || hostname.endsWith('.lovableproject.com')) {
    return null;
  }

  // Skip noknok.pro domains (handled by subdomain detection)
  if (hostname.endsWith('.noknok.pro') || hostname === 'noknok.pro') {
    return null;
  }

  // Skip www.noknok.pro
  if (hostname === 'www.noknok.pro') {
    return null;
  }

  // Skip pages.dev preview domains
  if (hostname.endsWith('.pages.dev')) {
    return null;
  }

  // This is a custom domain (e.g., joesroofing.com)
  return hostname;
}

export function getCurrentSubdomain(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return getSubdomainFromHostname(window.location.hostname);
}

export function getCurrentCustomDomain(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return getCustomDomainFromHostname(window.location.hostname);
}

/**
 * Get the store URL for a business
 * Returns subdomain format (e.g., subdomain.noknok.pro)
 */
export function getStoreUrl(subdomain: string): string {
  return `https://${subdomain}.noknok.pro`;
}
